<div class="main-view space-y-2 md:space-y-4">
  <app-page-surface-header
    titleIcon="notes"
    title="LLM Usage"
    subtitle="Every call made to a language model, with the tokens the provider reported and what it cost."
  />

  <div class="panel">
    <div class="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6">
      <input
        type="text"
        class="form-input"
        placeholder="PD ID or session code (e.g. PD-000001)"
        [(ngModel)]="pdSearch"
        (keyup.enter)="applyFilters()"
      />
      <input type="number" class="form-input" placeholder="Tenant id" [(ngModel)]="filters.tenant_id" />
      <input type="text" class="form-input" placeholder="Task code" [(ngModel)]="filters.task_code" />
      <select class="form-select" [(ngModel)]="filters.status">
        <option [ngValue]="undefined">Any status</option>
        @for (status of statuses; track status) {
        <option [ngValue]="status">{{ status }}</option>
        }
      </select>
      <select class="form-select" [(ngModel)]="filters.source">
        <option [ngValue]="undefined">Any source</option>
        @for (source of sources; track source) {
        <option [ngValue]="source">{{ source }}</option>
        }
      </select>
      <div class="flex gap-2">
        <button type="button" [class]="btn.primary + ' flex-1 justify-center'" (click)="applyFilters()">Filter</button>
        <button type="button" [class]="btn.secondary" (click)="resetFilters()">Reset</button>
      </div>
    </div>

    <div class="mb-3 flex items-center justify-between">
      <div class="text-sm text-white-dark">{{ meta.total | number }} calls</div>
      <button type="button" [class]="btn.smallSecondary" (click)="exportCsv()" [disabled]="!rows.length">
        Export page to CSV
      </button>
    </div>

    @if (error) {
    <div class="mb-4 rounded border border-danger/40 bg-danger-light p-3 text-danger">{{ error }}</div>
    } @if (loading) {
    <div class="py-10 text-center text-white-dark">Loading…</div>
    } @else {
    <div class="table-responsive">
      <table class="table-hover">
        <thead>
          <tr>
            <th>PD ID</th><th>Date/Time</th><th>Tenant</th><th>PD Link</th><th>Task</th><th>Model</th>
            <th class="text-right">In</th><th class="text-right">Out</th><th class="text-right">Cached</th>
            <th class="text-right">Total</th><th class="text-right">Cost</th><th class="text-right">ms</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          @for (row of rows; track row.id) {
          <tr>
            <td class="whitespace-nowrap">
              @if (row.pd_id) {
              <a
                [routerLink]="['/llm-pd-cost']"
                [queryParams]="{ session_code: row.pd_id }"
                class="font-medium text-primary hover:underline"
              >
                {{ row.pd_id }}
              </a>
              } @else {
              <span class="text-white-dark">—</span>
              }
            </td>
            <td class="whitespace-nowrap">
              <button type="button" class="text-primary hover:underline" title="Open call details"
                (click)="openCall(row.id)">
                {{ row.request_timestamp | date : 'dd MMM yy HH:mm' }}
              </button>
            </td>
            <td>
              {{ tenantLabel(row) }}
              @if (unattributedReason(row); as reason) {
              <span class="ml-1 badge bg-dark-light text-white-dark" [title]="reason">{{ row.source }}</span>
              }
            </td>
            <td>
              @if (row.session_code) {
              <a [routerLink]="['/llm-pd-cost']" [queryParams]="{ session_code: row.session_code }" class="text-primary hover:underline">
                {{ row.session_code }}
              </a>
              } @else {
              <span class="text-white-dark">—</span>
              }
            </td>
            <td>{{ row.task_code }}</td>
            <td>{{ row.model?.display_name || row.model_name }}</td>
            <td class="text-right">{{ row.input_tokens ?? '—' }}</td>
            <td class="text-right">{{ row.output_tokens ?? '—' }}</td>
            <td class="text-right">{{ row.cached_tokens ?? '—' }}</td>
            <td class="text-right">{{ row.total_tokens ?? '—' }}</td>
            <td class="text-right font-semibold">
              {{ formatUsd(row.total_cost_usd) }}
              <!-- No price covered this model when the call was made: the tokens are real,
                   the cost is not known. Showing it as $0.00 alone would read as free. -->
              @if (!row.cost_is_priced) {
              <span class="ml-1 badge bg-warning-light text-warning">unpriced</span>
              }
            </td>
            <td class="text-right">{{ row.processing_time_ms ?? '—' }}</td>
            <td><span [class]="statusClass(row.status)">{{ row.status }}</span></td>
          </tr>
          } @empty {
          <tr><td colspan="13" class="py-8 text-center text-white-dark">No calls match these filters.</td></tr>
          }
        </tbody>
      </table>
    </div>

    @if (meta.pages > 1) {
    <div class="mt-4 flex items-center justify-between">
      <button type="button" [class]="btn.smallSecondary" [disabled]="meta.page <= 1" (click)="goToPage(meta.page - 1)">
        Previous
      </button>
      <span class="text-sm text-white-dark">Page {{ meta.page }} of {{ meta.pages }}</span>
      <button type="button" [class]="btn.smallSecondary" [disabled]="meta.page >= meta.pages" (click)="goToPage(meta.page + 1)">
        Next
      </button>
    </div>
    }
    }
  </div>

  <app-call-detail-drawer [callId]="openCallId" (closed)="closeCall()" />
</div>
